import React from 'react';
import { BodyCell } from './TableCell';

// Скелетон первичной загрузки: плашка на месте будущего содержимого.
// Перенесён из аналитики (components/ui/Skeleton).
//
// Зачем вместо спиннера: спиннер не занимает места, поэтому страница
// собиралась в две ступени — пустой экран, потом сразу готовая раскладка, и всё
// под ней прыгало. Скелетон резервирует ровно ту же геометрию, что и готовый
// блок, поэтому переход к данным не двигает ничего.
//
// Правило для вызывающего кода: скелетон копирует раскладку конкретного блока
// (те же обёртки Card/сетки/высоты), а не «средний прямоугольник». Если под
// скелетон пришлось выдумать новую разметку — значит он не совпадёт с UI.
//
// Цвет — `line`, а не `fill`, как в аналитике: здесь `fill` почти совпадает с
// `surface` (242 против 248), и плашка внутри InfoCard или шапки таблицы
// пропадала. `line` различим и на card, и на surface в обеих темах.
//
// Под prefers-reduced-motion пульсация гаснет глобальным правилом в index.css
// (одна итерация) — плашка остаётся статичной, это нормально.

const RADIUS = {
  sm: 'rounded',
  md: 'rounded-lg',
  lg: 'rounded-xl',
  full: 'rounded-full',
};

// Базовый блок. width/height — любые CSS-значения (px, %, rem), потому что
// размеры скелетона задаются по месту и в токены не выносятся. height={null}
// отдаёт высоту классам — нужно, когда она разная на мобиле и десктопе.
const Skeleton = ({ width = '100%', height = 12, radius = 'sm', className = '', style }) => (
  <div
    aria-hidden="true"
    className={`bg-line animate-pulse shrink-0 ${RADIUS[radius] || RADIUS.sm} ${className}`}
    style={{ width, ...(height != null && { height }), ...style }}
  />
);

// Несколько строк текста: последняя короче, как в настоящем абзаце.
export const SkeletonText = ({ lines = 3, gap = 8, lineHeight = 12, lastWidth = '60%', className = '' }) => (
  <div className={`flex flex-col ${className}`} style={{ gap }}>
    {Array.from({ length: lines }, (_, index) => (
      <Skeleton
        key={index}
        height={lineHeight}
        width={index === lines - 1 ? lastWidth : '100%'}
      />
    ))}
  </div>
);

// Строки flex-таблицы на BodyCell: ширины берутся из того же COL, что и у
// настоящей таблицы, а ячейки — те же BodyCell, поэтому колонки скелетона стоят
// ровно под своими заголовками. column: { width, grow, first, sticky, align,
// fill (% ширины плашки), height, radius } или content — готовая разметка,
// когда ячейка сложнее одной плашки; без fill и content ячейка пустая.
export const SkeletonTableRows = ({ columns, rows = 8, rowClassName }) => (
  <div aria-hidden="true">
    {Array.from({ length: rows }, (_, rowIndex) => (
      <div key={rowIndex} className={rowClassName}>
        {columns.map((column, columnIndex) => (
          <BodyCell
            key={columnIndex}
            width={column.width}
            grow={column.grow}
            first={column.first}
            sticky={column.sticky}
            align={column.align}
          >
            {column.content || (column.fill && (
              <Skeleton
                width={`${column.fill}%`}
                height={column.height || 12}
                radius={column.radius}
              />
            ))}
          </BodyCell>
        ))}
      </div>
    ))}
  </div>
);

// Тулбар списков Servers/Apps: поиск, сортировка, сегменты статуса — те же
// h-8 / h-8 / 34px (на мобиле сегменты 44px под палец плюс рамка и паддинг).
export const SkeletonListToolbar = () => (
  <div className="shrink-0 flex flex-wrap items-center gap-3 p-4 border-b border-line">
    <Skeleton width={230} height={32} radius="md" className="max-w-full" />
    <Skeleton width={110} height={32} radius="md" />
    <Skeleton width={300} height={null} radius="md" className="h-[34px] max-md:h-[54px] max-w-full" />
  </div>
);

// Мобильная карточка списков Servers/Apps: заголовок с бейджем, четыре пары
// «подпись / значение», футер с кнопками 44px.
export const SkeletonListCard = ({ withIcon = false }) => (
  <div className="p-4" aria-hidden="true">
    <div className="flex justify-between items-start gap-3 mb-3">
      <div className="flex items-center gap-2.5 flex-1 min-w-0">
        {withIcon && <Skeleton width={40} height={40} radius="md" />}
        <div className="flex-1 min-w-0 flex flex-col gap-1.5 py-0.5">
          <Skeleton width="55%" height={16} />
          <Skeleton width="35%" height={12} />
        </div>
      </div>
      <Skeleton width={72} height={24} radius="full" />
    </div>
    <div className="flex flex-col gap-2">
      {[80, 40, 50, 50].map((width, index) => (
        <div key={index} className="flex flex-col gap-1">
          <Skeleton width={90} height={12} />
          <Skeleton width={`${width}%`} height={12} />
        </div>
      ))}
    </div>
    <div className="flex justify-between items-center pt-3 mt-3 border-t border-line">
      <Skeleton width={92} height={44} radius="md" />
      <Skeleton width={92} height={44} radius="md" />
    </div>
  </div>
);

// Шапка детальной страницы (PageHeader и шапка AppDetail): заголовок 32px и
// строка подписи под ним. Кнопка действий остаётся настоящей — ей данные не нужны.
export const SkeletonPageTitle = ({ withIcon = false }) => (
  <div className="flex items-center gap-3 min-w-0" aria-hidden="true">
    {withIcon && <Skeleton width={64} height={64} radius="lg" />}
    <div className="min-w-0 flex flex-col">
      <div className="h-8 flex items-center">
        <Skeleton width={220} height={22} className="max-w-full" />
      </div>
      <Skeleton width={160} height={12} className="mt-1 max-w-full" />
    </div>
  </div>
);

export default Skeleton;
